import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import classNames from 'classnames'
import { type FC, type ReactNode, useState } from 'react'

import { Select } from 'common/components/Select/Select'
import { getScheduleStaffColor } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'

import styles from './ehrAppointmentV2.module.scss'
import { ProviderMenu } from './ProviderMenu'
import { Avatar, PROVIDER_TEXT, type PersonOption } from './providerShared'

export { Avatar, PROVIDER_TEXT, type PersonOption } from './providerShared'

type Props = {
  // OpenEMR books several people on one visit; other sites book one provider.
  multiple: boolean
  options: PersonOption[]
  people: number[]
  // The row's own doctor, who stays on an edited visit.
  lockedId: number | null
  primaryId: number | null
  canMakePrimary: boolean
  isEditing?: boolean
  disabled?: boolean
  // Most people the visit can take.
  max?: number
  // Extra lines under the chips, inside the band.
  notes?: ReactNode
  onChange: (people: number[]) => void
  onMakePrimary: (id: number) => void
}

const countOf = (count: number): string =>
  count === 0 ? '' : count > 1 ? `${count} providers · joint appointment` : '1 provider'

export const ProviderField: FC<Props> = ({
  multiple,
  options,
  people,
  lockedId,
  primaryId,
  canMakePrimary,
  isEditing = false,
  disabled,
  max = Number.POSITIVE_INFINITY,
  notes,
  onChange,
  onMakePrimary,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const personOf = (id: number): PersonOption =>
    options.find((option) => option.id === id) ?? {
      id,
      name: `Provider #${id}`,
      role: '',
      color: getScheduleStaffColor(id),
    }

  if (!multiple) {
    return (
      <div className={styles.band}>
        <div className={styles.widthProvider}>
          <Select
            label={PROVIDER_TEXT.label}
            value={people[0]}
            showSearch={false}
            disabled={disabled}
            options={options.map((option) => ({ value: option.id, label: option.name }))}
            onChange={(next: number) => onChange([next])}
          />
        </div>
      </div>
    )
  }

  const isJoint = people.length > 1
  const isSingle = max === 1
  const isFull = people.length >= max
  const toggle = (id: number) => {
    if (people.includes(id)) {
      onChange(people.filter((person) => person !== id))
      return
    }
    // At the limit the newest pick replaces the oldest.
    onChange([...people, id].slice(-max))
    if (people.length + 1 >= max) setIsMenuOpen(false)
  }

  const menu = (
    <ProviderMenu
      ariaLabel={PROVIDER_TEXT.label}
      title={isSingle ? PROVIDER_TEXT.menuTitleOne : PROVIDER_TEXT.menuTitle}
      options={options}
      people={people}
      multiselect={!isSingle}
      disabledIds={lockedId === null ? [] : [lockedId]}
      onToggle={toggle}
      onDone={() => setIsMenuOpen(false)}
    />
  )

  return (
    <section
      className={classNames(styles.providerBand, {
        [styles.providerBandJoint]: isJoint,
        [styles.providerBandEmpty]: people.length === 0,
      })}
      aria-label={PROVIDER_TEXT.label}>
      <div className={styles.providerHeader}>
        <span className={styles.providerLabel}>
          {PROVIDER_TEXT.label} <span className={styles.providerRequired}>*</span>
        </span>
        <span className={styles.providerMuted}>{countOf(people.length)}</span>
      </div>

      {/* Only the Add provider button opens the menu; a click elsewhere closes it. */}
      <Dropdown
        open={isMenuOpen && !disabled}
        trigger={['click']}
        placement='bottomLeft'
        align={{ offset: [0, 8] }}
        popupRender={() => menu}
        onOpenChange={(next) => {
          if (!next) setIsMenuOpen(false)
        }}>
        <div className={styles.providerChips}>
          {people.map((id) => {
            const person = personOf(id)
            const { name } = person
            const isPrimary = isJoint && id === primaryId
            return (
              <span
                key={id}
                className={classNames(styles.providerChip, {
                  [styles.providerChipPrimary]: isPrimary,
                })}
                data-testid={`provider-chip-${name}`}>
                <Avatar person={person} />
                <span className={styles.providerName}>{name}</span>
                {isPrimary && (
                  <span className={styles.providerPrimary}>{PROVIDER_TEXT.primary}</span>
                )}
                {canMakePrimary && !isPrimary && !disabled && (
                  <button
                    type='button'
                    className={styles.providerLink}
                    aria-label={`${PROVIDER_TEXT.makePrimary}: ${name}`}
                    onClick={() => onMakePrimary(id)}>
                    {PROVIDER_TEXT.makePrimary}
                  </button>
                )}
                {id !== lockedId && !disabled && (
                  <button
                    type='button'
                    className={styles.providerRemove}
                    aria-label={`Remove ${name}`}
                    onClick={() => onChange(people.filter((person) => person !== id))}>
                    <CloseOutlined aria-hidden />
                  </button>
                )}
              </span>
            )
          })}
          {!disabled && !isFull && (
            <button
              type='button'
              className={styles.providerAdd}
              aria-haspopup='listbox'
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(true)}>
              <PlusOutlined aria-hidden />
              {PROVIDER_TEXT.addProvider}
            </button>
          )}
        </div>
      </Dropdown>

      {people.length === 0 && (
        <span className={styles.providerError}>{PROVIDER_TEXT.required}</span>
      )}
      {people.length === 1 && !isEditing && !isFull && (
        <span className={styles.providerMuted}>{PROVIDER_TEXT.addMore}</span>
      )}
      {isEditing && isJoint && (
        <span className={styles.providerMuted}>{PROVIDER_TEXT.jointEdit}</span>
      )}
      {notes}
    </section>
  )
}
